import React, { useMemo, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { useLingui } from '@lingui/react';
import WorkpageApi from '../api/WorkpageApi';
import LookupApi from '../api/LookupApi';
import RedirectLoader, { isRedirectPath } from '../components/RedirectLoader';
import WorkpageShellSkeleton from '../components/WorkpageShellSkeleton';
import RedirectLoaderScreen from '../components/RedirectLoaderScreen';
import useResourceLoader from '../util/useResourceLoader';
import { AuthContext, AuthContextValue } from './AuthContext';
import { Workpage } from '../api/ManageWorkpageApi';
import { localizeWorkpageRecord } from '../util/workpageI18n';

// ── Domain types ─────────────────────────────────────────────────────────────

/** Workpage enriched with the runtime `default` flag set on the first item. */
export type WorkpageDef = Workpage & { default?: boolean };

// ── Context value ─────────────────────────────────────────────────────────────

export interface UWEContextValue {
  workpages: WorkpageDef[];
  adminWorkpages: WorkpageDef[];
  hasAdminWorkpages: boolean;
  appOptions: Record<string, unknown> | null | undefined;
}

// Augment the plain Context type to carry the .Component convenience property
interface UWEContextType extends React.Context<UWEContextValue | undefined> {
  Component: React.FC<{ children: React.ReactNode }>;
}

// ── Context + provider component ─────────────────────────────────────────────

const UWEContext = React.createContext<UWEContextValue | undefined>(undefined) as UWEContextType;

UWEContext.Component = function UWEContext_Component({ children }: { children: React.ReactNode }) {
  const { value, loading } = useUWEContextComponentHooks();
  const location = useLocation();

  const fallback = isRedirectPath(location.pathname) ? (
    <RedirectLoaderScreen>
      <RedirectLoader />
    </RedirectLoaderScreen>
  ) : (
    <WorkpageShellSkeleton />
  );

  return <UWEContext.Provider value={value}>{loading !== false ? fallback : children}</UWEContext.Provider>;
};

// ── Internal hook ─────────────────────────────────────────────────────────────

interface HookResult {
  value: UWEContextValue;
  loading: boolean | undefined;
  error: Error | undefined;
}

function useUWEContextComponentHooks(): HookResult {
  const auth = useContext(AuthContext) as AuthContextValue;
  const { i18n } = useLingui();
  const location = useLocation();

  // `/redirect/*` routes bounce the user straight to an external URL and never render
  // workpage-driven UI (sidebar, breadcrumbs, etc.), so skip these fetches entirely there
  // rather than block the redirect on data it'll never use.
  const skipAppData = isRedirectPath(location.pathname);

  const [allWorkpages, loadingAllWorkpages, errorLoadingWorkpages] = useResourceLoader<WorkpageDef[]>(() => {
    if (skipAppData) return Promise.resolve(useResourceLoader.DoNotSet);
    if (auth.user) {
      return WorkpageApi.list().then((data: WorkpageDef[]) => {
        if (data?.length) data[0].default = true;
        return data;
      });
    }
    return Promise.resolve([]);
  }, [auth, skipAppData]);

  // Localized on top of the (locale-independent) fetch, not baked into it — so
  // toggling the app language updates sidebar/breadcrumb names immediately
  // instead of only after the next re-fetch.
  const localizedWorkpages = useMemo(() => allWorkpages?.map(wp => localizeWorkpageRecord(wp, i18n.locale)) ?? [], [allWorkpages, i18n.locale]);

  // Memoized so these keep a stable reference across re-renders that don't actually
  // change the workpage list (e.g. this provider re-rendering on every route change,
  // including query-string-only navigations like a WorkTray filter/pagination update) —
  // otherwise consumers like SystemSettings recompute tabs and remount the active tab.
  const workpages: WorkpageDef[] = useMemo(() => localizedWorkpages.filter(wp => !wp.isAdmin), [localizedWorkpages]);
  const adminWorkpages: WorkpageDef[] = useMemo(() => localizedWorkpages.filter(wp => wp.isAdmin), [localizedWorkpages]);
  const hasAdminWorkpages = adminWorkpages.length > 0;

  const [appOptions, loadingAppOptions, errorLoadingAppOptions] = useResourceLoader<Record<string, unknown>>(async () => {
    if (skipAppData) return useResourceLoader.DoNotSet;
    if (!auth.user) return null;
    return (await LookupApi.getAppOptions()).data;
  }, [skipAppData, auth]);

  const loading = loadingAllWorkpages || loadingAppOptions;
  const error = errorLoadingWorkpages ?? errorLoadingAppOptions;

  const value = useMemo<UWEContextValue>(
    () => ({
      workpages,
      adminWorkpages,
      hasAdminWorkpages,
      appOptions,
    }),
    [workpages, adminWorkpages, hasAdminWorkpages, appOptions]
  );

  return { value, loading, error };
}

export default UWEContext;
